// SolicitudAccesoModal.js — Formulario emergente «Solicitar acceso al programa».
//
// Es un prerregistro, no un registro: no crea cuenta ni inicia sesión. Los datos
// van a `api/solicitud-acceso.php`, que los guarda y avisa por correo al equipo
// del proyecto (ficha del proyecto → Solicitudes).
//
// Con `modo="registro"` es el registro de la web con el mismo diseño: la cuenta
// se crea al enviarlo, la sesión queda iniciada y se entra a la plataforma (sin
// esperar la respuesta del equipo). Sus textos son las claves `registro_*`.
//
// Todo lo visible sale de la config de la sección `solicitud_acceso` con
// valores por defecto iguales al prerregistro de LAAC Academy, así que una
// sección sin configurar ya muestra el formulario completo.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { solicitudAccesoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { PAISES_CODIGOS } from '../../utils/countryCodes';
import TerminosPopup, { useTerminos } from '../TerminosPopup';

export const ESPECIALIDADES_DEFECTO = ['Nefrología', 'Neurología', 'Gastroenterología', 'Cardiología', 'Otra'];

const PAISES_DEFECTO = PAISES_CODIGOS.map(p => p.nombre).sort((a, b) => a.localeCompare(b, 'es'));

// Campos del formulario, en el orden en que se pintan (rejilla de 2 columnas).
export const CAMPOS_DEFECTO = [
  { key: 'nombres', label: 'Nombres', tipo: 'text', placeholder: 'Ej. María Alejandra', requerido: true, autoComplete: 'given-name' },
  { key: 'apellidos', label: 'Apellidos', tipo: 'text', placeholder: 'Ej. Rodríguez Gómez', requerido: true, autoComplete: 'family-name' },
  { key: 'email', label: 'Correo electrónico', tipo: 'email', placeholder: 'correo@ejemplo.com', requerido: true, autoComplete: 'email' },
  { key: 'telefono', label: 'Teléfono / WhatsApp', tipo: 'tel', placeholder: '+57 300 000 0000', requerido: true, autoComplete: 'tel' },
  { key: 'documento', label: 'Cédula', tipo: 'numero', placeholder: 'Solo números', requerido: true },
  { key: 'pais', label: 'País', tipo: 'pais', requerido: true },
  { key: 'especialidad', label: 'Especialidad', tipo: 'especialidad', requerido: true },
  { key: 'institucion', label: 'Institución', tipo: 'text', placeholder: 'Hospital, clínica o universidad donde ejerces', requerido: false, autoComplete: 'organization' },
  { key: 'password', label: 'Contraseña', tipo: 'password', placeholder: 'Mínimo 5 caracteres', requerido: true, autoComplete: 'new-password' },
];

// Campos del registro de la web (modo «registro»): los del registro de LAAC
// Academy. Cada proyecto los ajusta en `config.registro_campos` (etiqueta,
// obligatorio, visible y, en las listas, sus opciones).
export const CAMPOS_REGISTRO_DEFECTO = [
  { key: 'nombres', label: 'Nombres', tipo: 'text', requerido: true, autoComplete: 'given-name' },
  { key: 'apellidos', label: 'Apellidos', tipo: 'text', requerido: true, autoComplete: 'family-name' },
  { key: 'email', label: 'Correo electrónico', tipo: 'email', requerido: true, autoComplete: 'email' },
  { key: 'documento', label: 'Cédula', tipo: 'numero', requerido: false },
  { key: 'telefono', label: 'Teléfono', tipo: 'tel', requerido: true, autoComplete: 'tel' },
  { key: 'password', label: 'Contraseña', tipo: 'password', requerido: true, autoComplete: 'new-password' },
  { key: 'institucion', label: 'Institución', tipo: 'select', requerido: false, opciones: [] },
  { key: 'pais', label: 'País', tipo: 'select', requerido: false, placeholder: 'Elige un país' },
  { key: 'especialidad', label: 'Especialidad', tipo: 'select', requerido: false, opciones: [] },
  { key: 'acepto_terminos', label: 'Acepto términos y condiciones', tipo: 'checkbox', requerido: true },
];

// Sin estos no se puede crear una cuenta. La contraseña se puede ocultar: en
// proyectos que entran solo con el correo el servidor pone una al azar.
const IMPRESCINDIBLES_REGISTRO = ['nombres', 'email'];

/** Campos del registro: los de por defecto con lo que el proyecto haya cambiado. */
export const resolverCamposRegistro = (config = {}, tl = (v) => v) => {
  const propios = Array.isArray(config.registro_campos) ? config.registro_campos : [];
  const porKey = Object.fromEntries(propios.filter(c => c && c.key).map(c => [c.key, c]));
  const orden = propios.length
    ? [...propios.map(c => c.key), ...CAMPOS_REGISTRO_DEFECTO.map(c => c.key).filter(k => !porKey[k])]
    : CAMPOS_REGISTRO_DEFECTO.map(c => c.key);
  return orden
    .map(k => {
      const base = CAMPOS_REGISTRO_DEFECTO.find(c => c.key === k);
      if (!base) return null;
      const propio = porKey[k] || {};
      const imprescindible = IMPRESCINDIBLES_REGISTRO.includes(k);
      return {
        ...base,
        ...propio,
        label: tl(propio.label) || base.label,
        placeholder: tl(propio.placeholder) || base.placeholder,
        opciones: Array.isArray(propio.opciones) && propio.opciones.length ? propio.opciones : base.opciones,
        visible: imprescindible ? true : propio.visible !== false,
        requerido: imprescindible ? true : (propio.requerido ?? base.requerido),
      };
    })
    .filter(c => c && c.visible);
};

const OTRA = 'Otra';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const subir = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(18, 10, 34, .46);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  z-index: 999999;
  animation: ${fadeIn} .18s ease-out;
  overflow-y: auto;
  @media (max-width: 767px) { padding: 12px; }
`;

const Modal = styled.div`
  --sa-primario: ${p => p.$primario};
  --sa-acento: ${p => p.$acento};
  position: relative;
  width: min(100%, 690px);
  max-height: calc(100vh - 40px);
  overflow: auto;
  background: #fff;
  border-radius: 18px;
  padding: 18px 30px 28px;
  box-shadow: 0 30px 80px rgba(24, 10, 52, .28);
  border: 1px solid rgba(124, 58, 237, .12);
  text-align: center;
  color: #1d1240;
  font-family: ${p => p.$fuente};
  box-sizing: border-box;
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-track { background: #f1ebf7; border-radius: 99px; }
  &::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--sa-acento), var(--sa-primario)); border-radius: 99px; }
  *, *::before, *::after { box-sizing: border-box; }
  @media (max-width: 767px) {
    padding: 16px 16px 22px;
    max-height: calc(100vh - 24px);
    border-radius: 16px;
  }
`;

const Cerrar = styled.button`
  position: absolute;
  top: 10px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #7c6e8f;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  border-radius: 8px;
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--sa-acento); outline-offset: 2px; }
`;

const Icono = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 2px auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #d94df3 0%, #7d35df 100%);
  color: #fff;
  font-size: 27px;
  box-shadow: 0 10px 30px rgba(168, 85, 247, .28);
`;

const Titulo = styled.h2`
  margin: 0 0 12px;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: -.02em;
  color: #1b0b43;
  @media (max-width: 767px) { font-size: 22px; padding: 0 24px; }
`;

const Subtitulo = styled.p`
  margin: 0 auto 22px;
  max-width: 560px;
  color: #7a6f92;
  font-size: 18px;
  line-height: 1.45;
  strong { color: #1b0b43; font-weight: 800; }
  @media (max-width: 767px) { font-size: 16px; }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  border: 1px solid #e6ddef;
  background: #fbf9fd;
  color: #28124c;
  border-radius: 12px;
  padding: 15px 16px;
  font-size: 16px;
  margin: 0 0 18px;
  strong { color: #1b0b43; font-weight: 800; }
`;

const InfoIcono = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid #00c9df;
  color: #00c9df;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex: 0 0 18px;
`;

const Form = styled.form`
  text-align: left;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
  @media (max-width: 767px) { grid-template-columns: 1fr; gap: 12px; }
`;

const Label = styled.label`
  display: block;
  font-size: 17px;
  line-height: 1.25;
  color: #19073f;
  font-weight: 500;
  margin: 0 0 8px;
  @media (max-width: 767px) { font-size: 16px; }
`;

const flecha = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%239183a6' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

const campoCss = `
  width: 100%;
  height: 36px;
  border: 1px solid #e2d9ea;
  border-radius: 10px;
  background-color: #fff;
  padding: 0 13px;
  font-size: 16px;
  font-family: inherit;
  color: #2b1a52;
  outline: none;
  margin: 0;
  transition: border-color .18s ease, box-shadow .18s ease;
  &::placeholder { color: #9183a6; }
  &:focus { border-color: #b98ef6; box-shadow: 0 0 0 4px rgba(168, 85, 247, .12); }
  &[data-invalido='true'] { border-color: #ef6a8a; box-shadow: 0 0 0 4px rgba(239, 106, 138, .12); }
  @media (max-width: 767px) { height: 50px; font-size: 15px; }
`;

const Input = styled.input`${campoCss}`;

const Select = styled.select`
  ${campoCss}
  appearance: none;
  -webkit-appearance: none;
  background-image: ${flecha};
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 42px;
`;

const PasswordWrap = styled.div`
  position: relative;
  input { padding-right: 52px; }
`;

const Ojo = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  color: var(--sa-primario);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  padding: 0;
  &:hover, &:focus-visible { background: rgba(168, 85, 247, .08); outline: none; }
  svg { width: 20px; height: 20px; display: block; }
`;

const Casilla = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #19073f;
  cursor: pointer;
  padding: 4px 0;
  input { width: 18px; height: 18px; margin: 0; accent-color: var(--sa-primario); flex: 0 0 18px; cursor: pointer; }
  a { color: var(--sa-primario); }
  .ver-terminos {
    border: none; background: none; padding: 0; font: inherit; text-align: left; cursor: pointer;
    color: var(--sa-primario); font-weight: 600; text-decoration: underline; text-underline-offset: 2px;
  }
  &[data-invalido='true'] { color: #be123c; }
  &[data-invalido='true'] input { outline: 2px solid #ef6a8a; outline-offset: 2px; }
`;

const Enviar = styled.button`
  margin-top: 20px;
  width: 100%;
  height: 54px;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  background: linear-gradient(90deg, #d946ef 0%, #7c3aed 100%);
  box-shadow: 0 14px 30px rgba(168, 85, 247, .28);
  transition: filter .2s ease, transform .2s ease;
  &:hover:not(:disabled) { filter: brightness(1.05); transform: translateY(-1px); }
  &:disabled { opacity: .75; cursor: not-allowed; }
  @media (max-width: 767px) { height: 52px; font-size: 16px; }
`;

const Pie = styled.p`
  text-align: center;
  color: #7f738f;
  font-size: 14px;
  line-height: 1.5;
  margin: 18px 0 0;
`;

const Mensaje = styled.div`
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.45;
  ${p => p.$tipo === 'info'
    ? 'background:#f5f3ff;color:#5b21b6;border:1px solid #ddd6fe;'
    : 'background:#fff1f2;color:#be123c;border:1px solid #fecdd3;'}
`;

const Exito = styled.div`
  text-align: center;
  padding: 28px 12px 10px;
  animation: ${subir} .25s ease;
  .check {
    width: 72px;
    height: 72px;
    margin: 0 auto 16px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--sa-primario), var(--sa-acento));
    color: #fff;
    font-size: 34px;
    font-weight: 800;
    box-shadow: 0 14px 30px rgba(124, 58, 237, .24);
  }
  h3 { margin: 0 0 10px; font-size: 24px; line-height: 1.1; font-weight: 800; color: #111827; }
  p { margin: 0; font-size: 15px; line-height: 1.5; color: #4b5563; }
  strong { color: #1b0b43; }
`;

// Campo trampa para bots: fuera de la vista y del tabulador.
const Trampa = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const OjoAbierto = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 12C3.73 8.11 7.52 5.5 12 5.5C16.48 5.5 20.27 8.11 22 12C20.27 15.89 16.48 18.5 12 18.5C7.52 18.5 3.73 15.89 2 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const OjoCerrado = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10.58 10.58C10.21 10.95 10 11.46 10 12C10 13.1 10.9 14 12 14C12.54 14 13.05 13.79 13.42 13.42" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.88 5.09C10.57 4.87 11.27 4.75 12 4.75C16.8 4.75 20.85 7.75 22.5 12C21.82 13.74 20.67 15.25 19.2 16.37" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.1 6.1C4.55 7.22 3.33 8.82 2.5 12C3.93 15.66 7.29 18.35 11.31 18.72" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Resalta en negrita el tiempo de respuesta dentro de un texto plano.
const conNegrita = (texto, resaltado) => {
  if (!resaltado || !texto || !texto.includes(resaltado)) return texto;
  const [antes, ...resto] = texto.split(resaltado);
  return <>{antes}<strong>{resaltado}</strong>{resto.join(resaltado)}</>;
};

/** Campos efectivos: los de por defecto con las etiquetas/visibilidad de la config. */
export const resolverCampos = (config = {}, tl = (v) => v) => {
  const ocultos = Array.isArray(config.campos_ocultos) ? config.campos_ocultos : [];
  const opcionales = Array.isArray(config.campos_opcionales) ? config.campos_opcionales : [];
  const etiquetas = config.etiquetas || {};
  const placeholders = config.placeholders || {};
  return CAMPOS_DEFECTO
    // Nombre y correo son lo mínimo para poder contactar a la persona.
    .filter(c => ['nombres', 'email'].includes(c.key) || !ocultos.includes(c.key))
    .map(c => ({
      ...c,
      label: tl(etiquetas[c.key]) || c.label,
      placeholder: tl(placeholders[c.key]) || c.placeholder,
      requerido: ['nombres', 'email'].includes(c.key) ? true : (c.requerido && !opcionales.includes(c.key)),
    }));
};

const SolicitudAccesoModal = ({ config = {}, proyecto, onClose, modo = 'solicitud', tl = (v, f = '') => (v == null ? f : v) }) => {
  const esRegistro = modo === 'registro';
  // Fuera del proveedor de sesión (p. ej. en pruebas) el prerregistro sigue funcionando.
  const { login } = useAuth() || {};
  const esRegistroCampos = modo === 'registro';
  const campos = useMemo(
    () => (esRegistroCampos ? resolverCamposRegistro(config, tl) : resolverCampos(config, tl)),
    [config, tl, esRegistroCampos]
  );
  const especialidades = Array.isArray(config.especialidades) && config.especialidades.length
    ? config.especialidades : ESPECIALIDADES_DEFECTO;
  const paises = Array.isArray(config.paises) && config.paises.length ? config.paises : PAISES_DEFECTO;
  const paisDefecto = config.pais_defecto ?? 'Colombia';
  const tiempo = tl(config.tiempo_respuesta, '48 horas hábiles');

  const [valores, setValores] = useState(() => ({ pais: paises.includes(paisDefecto) ? paisDefecto : '' }));
  const [invalidos, setInvalidos] = useState({});
  const [mensaje, setMensaje] = useState(null);
  const [verPassword, setVerPassword] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  // Pop up con el texto de los términos (se abre desde «Acepto términos y condiciones»).
  const [verTerminos, setVerTerminos] = useState(false);
  const terminos = useTerminos();
  const cerrarTerminos = useCallback(() => setVerTerminos(false), []);
  const refs = useRef({});
  const cerrarRef = useRef(null);

  // Bloquea el scroll de la página y cierra con Escape.
  useEffect(() => {
    const html = document.documentElement;
    const previo = html.style.overflow;
    html.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    cerrarRef.current?.focus();
    return () => {
      html.style.overflow = previo;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Tras enviar el prerregistro, se cierra solo (el registro entra a la plataforma).
  useEffect(() => {
    if (!enviado || esRegistro) return undefined;
    const t = setTimeout(() => onClose?.(), Number(config.cerrar_tras_ms) || 4000);
    return () => clearTimeout(t);
  }, [enviado, onClose, config.cerrar_tras_ms, esRegistro]);

  const set = (k, v) => {
    setValores(prev => ({ ...prev, [k]: v }));
    if (invalidos[k]) setInvalidos(prev => ({ ...prev, [k]: false }));
  };

  const fallar = (key, texto) => {
    setMensaje({ tipo: 'error', texto });
    setInvalidos(prev => ({ ...prev, [key]: true }));
    refs.current[key]?.focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMensaje(null);
    const v = (k) => String(valores[k] ?? '').trim();

    const faltantes = {};
    campos.forEach(c => {
      if (c.requerido && (c.tipo === 'checkbox' ? valores[c.key] !== true : !v(c.key))) faltantes[c.key] = true;
    });
    ['especialidad', 'institucion'].forEach(k => {
      if (v(k) === OTRA && !v(`${k}_otra`)) faltantes[`${k}_otra`] = true;
    });
    const primero = campos.flatMap(c => [c.key, `${c.key}_otra`]).find(k => faltantes[k]);
    if (primero) {
      setInvalidos(faltantes);
      setMensaje({
        tipo: 'error',
        texto: esRegistro
          ? tl(config.registro_texto_obligatorios, 'Debes completar los campos obligatorios antes de continuar.')
          : 'Por favor completa los campos obligatorios.',
      });
      refs.current[primero]?.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v('email'))) return fallar('email', 'Por favor ingresa un correo electrónico válido.');
    if (v('telefono') && v('telefono').replace(/\D/g, '').length < 7) return fallar('telefono', 'Por favor ingresa un teléfono o WhatsApp válido.');
    if (v('documento') && !/^\d+$/.test(v('documento'))) return fallar('documento', 'La cédula debe contener solo números.');
    if (campos.some(c => c.key === 'password') && (v('password') || campos.find(c => c.key === 'password').requerido)
      && v('password').length < 5) return fallar('password', 'La contraseña debe tener mínimo 5 caracteres.');

    setEnviando(true);
    try {
      const res = await solicitudAccesoService.enviar({
        proyecto_id: proyecto?.id,
        nombres: v('nombres'),
        apellidos: v('apellidos'),
        email: v('email'),
        telefono: v('telefono'),
        documento: v('documento'),
        pais: v('pais'),
        especialidad: v('especialidad') === OTRA ? v('especialidad_otra') : v('especialidad'),
        institucion: v('institucion') === OTRA ? v('institucion_otra') : v('institucion'),
        password: valores.password || '',
        extra: esRegistro && campos.some(c => c.key === 'acepto_terminos')
          ? { acepto_terminos: valores.acepto_terminos === true ? 'Sí' : 'No' }
          : undefined,
        modo: esRegistro ? 'registro' : undefined,
        sitio_web: valores.sitio_web || '',
        origen: typeof window !== 'undefined' ? window.location.href.slice(0, 250) : '',
      });
      if (res.data?.success && esRegistro && res.data.token) {
        setEnviado(true);
        // Se deja ver «¡Tu cuenta está lista!» y luego se entra con la sesión nueva.
        // Va fuera del ciclo del modal: iniciar sesión vuelve a pintar la página y
        // lo desmontaría antes de tiempo.
        const destino = config.registro_url_destino || proyecto?.config_json?.auth_modal?.url_destino || '/contenidos';
        const { token, user } = res.data;
        setTimeout(() => {
          if (login) login({ token, user });
          window.location.assign(destino);
        }, 1800);
      } else if (res.data?.success) setEnviado(true);
      else setMensaje({ tipo: 'error', texto: res.data?.message || 'No se pudo guardar. Intenta nuevamente.' });
    } catch (err) {
      const d = err.response?.data;
      setMensaje({
        tipo: (d?.code === 'exists' || d?.code === 'cuenta_existe') ? 'info' : 'error',
        texto: d?.message || 'No se pudo guardar el preregistro. Intenta nuevamente.',
      });
    } finally {
      setEnviando(false);
    }
  };

  const control = (c) => {
    const comunes = {
      id: `sa_${c.key}`,
      name: c.key,
      ref: (el) => { refs.current[c.key] = el; },
      value: valores[c.key] ?? '',
      'data-invalido': invalidos[c.key] ? 'true' : 'false',
      'aria-invalid': invalidos[c.key] ? 'true' : undefined,
      required: c.requerido,
      onChange: (e) => set(c.key, e.target.value),
    };
    if (c.tipo === 'checkbox') {
      return (
        <Casilla data-invalido={invalidos[c.key] ? 'true' : 'false'}>
          <input
            type="checkbox"
            id={`sa_${c.key}`}
            ref={(el) => { refs.current[c.key] = el; }}
            checked={valores[c.key] === true}
            aria-invalid={invalidos[c.key] ? 'true' : undefined}
            onChange={(e) => set(c.key, e.target.checked)}
          />
          <span>
            {c.key === 'acepto_terminos' && terminos.hay ? (
              <button type="button" className="ver-terminos" onClick={(e) => { e.preventDefault(); setVerTerminos(true); }}>
                {c.label}
              </button>
            ) : c.enlace_url
              ? <a href={c.enlace_url} target="_blank" rel="noopener noreferrer">{c.label}</a>
              : c.label}
          </span>
        </Casilla>
      );
    }
    if (c.tipo === 'select') {
      const opciones = c.opciones?.length
        ? c.opciones
        : (c.key === 'pais' ? paises : (c.key === 'especialidad' ? especialidades : []));
      if (!opciones.length) {
        return <Input {...comunes} type="text" placeholder={c.placeholder} autoComplete={c.autoComplete} />;
      }
      return (
        <Select {...comunes}>
          <option value="">{c.placeholder || 'Selecciona una opción'}</option>
          {opciones.map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    }
    if (c.tipo === 'pais' || c.tipo === 'especialidad') {
      const opciones = c.tipo === 'pais' ? paises : especialidades;
      return (
        <Select {...comunes}>
          <option value="">{c.tipo === 'pais' ? 'Selecciona tu país' : 'Selecciona tu especialidad'}</option>
          {opciones.map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    }
    if (c.tipo === 'password') {
      return (
        <PasswordWrap>
          <Input {...comunes} type={verPassword ? 'text' : 'password'} placeholder={c.placeholder} autoComplete={c.autoComplete} minLength={5} maxLength={72} />
          <Ojo
            type="button"
            aria-label={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            aria-pressed={verPassword}
            onClick={() => setVerPassword(x => !x)}
          >
            {verPassword ? <OjoCerrado /> : <OjoAbierto />}
          </Ojo>
        </PasswordWrap>
      );
    }
    return (
      <Input
        {...comunes}
        type={c.tipo === 'numero' ? 'text' : c.tipo}
        inputMode={c.tipo === 'numero' ? 'numeric' : (c.tipo === 'tel' ? 'tel' : undefined)}
        placeholder={c.placeholder}
        autoComplete={c.autoComplete}
      />
    );
  };

  // El registro de la web marca los obligatorios con «(*)».
  const marcaObligatorio = esRegistro ? tl(config.registro_marca_obligatorio, ' (*)') : ' *';
  const primario = config.color_primario || '#7c3aed';
  const acento = config.color_acento || '#a855f7';

  const contenido = (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa_titulo"
        $primario={primario}
        $acento={acento}
        $fuente={config.fuente || "'Roboto', Arial, Helvetica, sans-serif"}
      >
        <Cerrar ref={cerrarRef} type="button" aria-label="Cerrar" onClick={() => onClose?.()}>×</Cerrar>
        <Icono aria-hidden="true">{config.icono || '🫀'}</Icono>
        <Titulo id="sa_titulo">
          {esRegistro ? tl(config.registro_titulo, 'Crear cuenta') : tl(config.titulo, 'Prerregistro')}
        </Titulo>
        <Subtitulo>
          {esRegistro
            ? tl(config.registro_subtitulo, 'Completa tus datos y accede de inmediato a la plataforma.')
            : conNegrita(
              tl(config.subtitulo, '¿Te interesa registrarte? Déjanos tus datos y nos comunicaremos contigo en un máximo de 48 horas hábiles.'),
              tiempo
            )}
        </Subtitulo>
        {!esRegistro && config.mostrar_tiempo_respuesta !== false && tiempo && (
          <InfoBox>
            <InfoIcono aria-hidden="true">◔</InfoIcono>
            <span>{tl(config.etiqueta_tiempo, 'Tiempo de respuesta')}: <strong>{tiempo}</strong></span>
          </InfoBox>
        )}

        {enviado ? (
          <Exito role="status">
            <div className="check">✓</div>
            {esRegistro ? (
              <>
                <h3>{tl(config.registro_exito_titulo, '¡Tu cuenta está lista!')}</h3>
                <p>{tl(config.registro_exito_texto, 'Ya tienes acceso. Te llevamos a la plataforma…')}</p>
              </>
            ) : (
              <>
                <h3>{tl(config.exito_titulo, '¡Solicitud enviada correctamente!')}</h3>
                <p>
                  {conNegrita(
                    tl(config.exito_texto, `Nuestro equipo revisará tu información y en un máximo de ${tiempo} se pondrá en contacto contigo.`),
                    tiempo
                  )}
                </p>
              </>
            )}
          </Exito>
        ) : (
          <Form onSubmit={onSubmit} noValidate autoComplete="on">
            {mensaje && <Mensaje $tipo={mensaje.tipo} role="alert">{mensaje.texto}</Mensaje>}
            <Trampa aria-hidden="true">
              <label htmlFor="sa_sitio_web">Sitio web</label>
              <input id="sa_sitio_web" name="sitio_web" tabIndex={-1} autoComplete="off"
                value={valores.sitio_web || ''} onChange={e => set('sitio_web', e.target.value)} />
            </Trampa>
            <Grid>
              {campos.map(c => (
                <React.Fragment key={c.key}>
                  {c.tipo === 'checkbox' ? (
                    <div style={{ gridColumn: '1 / -1' }}>{control(c)}</div>
                  ) : (
                    <div>
                      <Label htmlFor={`sa_${c.key}`}>{c.label}{c.requerido ? marcaObligatorio : ''}</Label>
                      {control(c)}
                    </div>
                  )}
                  {['especialidad', 'institucion'].includes(c.key) && valores[c.key] === OTRA && (
                    <div>
                      <Label htmlFor={`sa_${c.key}_otra`}>
                        {c.key === 'especialidad' ? 'Especifica tu especialidad' : 'Especifica tu institución'}{marcaObligatorio}
                      </Label>
                      <Input
                        id={`sa_${c.key}_otra`}
                        ref={(el) => { refs.current[`${c.key}_otra`] = el; }}
                        value={valores[`${c.key}_otra`] || ''}
                        data-invalido={invalidos[`${c.key}_otra`] ? 'true' : 'false'}
                        placeholder={c.key === 'especialidad' ? 'Escribe tu especialidad' : 'Escribe tu institución'}
                        onChange={e => set(`${c.key}_otra`, e.target.value)}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </Grid>
            <Enviar type="submit" disabled={enviando}>
              <span aria-hidden="true" style={{ fontSize: 18 }}>✈</span>
              <span>{enviando
                ? (esRegistro ? 'Creando cuenta…' : 'Enviando…')
                : (esRegistro ? tl(config.registro_texto_enviar, 'Crear cuenta') : tl(config.texto_enviar, 'Enviar solicitud'))}</span>
            </Enviar>
            <Pie>{tl(config.texto_pie, 'Tu información es confidencial y no será compartida con terceros.')}</Pie>
          </Form>
        )}
      </Modal>
      <TerminosPopup abierto={verTerminos} onCerrar={cerrarTerminos} onAceptar={() => set('acepto_terminos', true)} />
    </Overlay>
  );

  return createPortal(contenido, document.body);
};

export default SolicitudAccesoModal;
